import * as RadixTabs from '@radix-ui/react-tabs';
import { Fragment, memo } from 'react';

import { cn } from '@air/utils-tailwind';

export type PanelTab<T extends string = string> = {
  id: T;
  label: string;
  testId?: string;
  value: T;
};

export type PanelTabsListProps = {
  singleTabClassName?: string;
  singleTabDisplay?: 'hidden' | 'title';
  tabs: PanelTab[];
  tabsListTestId?: string;
  triggerTestId?: string;
};

export const PanelTabsList = memo(
  ({
    tabs,
    singleTabClassName,
    singleTabDisplay = 'hidden',
    triggerTestId = 'PANEL_TABS_LIST_TRIGGER',
    tabsListTestId,
  }: PanelTabsListProps) => {
    if (tabs.length === 1) {
      const tab = tabs[0];

      if (singleTabDisplay === 'title') {
        return (
          <Fragment>
            <RadixTabs.List className="sr-only" data-testid={tabsListTestId} tabIndex={-1}>
              <RadixTabs.Trigger
                className={cn('font-medium ads:text-primary text-14 text-gray-12', singleTabClassName)}
                data-tab-id={tab.id}
                tabIndex={-1}
                value={tab.value}
              >
                {tab.label}
              </RadixTabs.Trigger>
            </RadixTabs.List>
            <span
              aria-hidden
              className={cn('font-medium ads:text-primary text-14 text-gray-12', singleTabClassName)}
              data-tab-id={tab.id}
              data-testid={tab.testId ?? triggerTestId}
            >
              {tab.label}
            </span>
          </Fragment>
        );
      }

      return (
        <Fragment>
          <RadixTabs.List className="sr-only focus-within:not-sr-only" data-testid={tabsListTestId}>
            <RadixTabs.Trigger
              className="font-medium ads:text-primary text-14 text-gray-12 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9"
              data-tab-id={tab.id}
              data-testid={tab.testId ?? triggerTestId}
              value={tab.value}
            >
              {tab.label}
            </RadixTabs.Trigger>
          </RadixTabs.List>
          <div />
        </Fragment>
      );
    }

    return (
      <RadixTabs.List
        className="flex items-center gap-1 rounded-8 ads:bg-ads-gray-4 bg-gray-4 p-1"
        data-testid={tabsListTestId}
      >
        {tabs.map((tab) => (
          <RadixTabs.Trigger
            className="h-5 rounded-4 ads:bg-ads-gray-4 bg-gray-4 px-1.5 font-medium ads:text-metadata text-12 text-gray-10 outline-none transition-colors ads:hover:bg-hover-subtle hover:bg-gray-5 ads:hover:text-primary hover:text-gray-12 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 ads:data-[state=active]:bg-base data-[state=active]:bg-gray-1 ads:data-[state=active]:text-primary data-[state=active]:text-gray-12 data-[state=active]:focus-visible:z-10 ads:data-[state=active]:hover:bg-base data-[state=active]:hover:bg-gray-1"
            data-tab-id={tab.id}
            data-testid={tab.testId ?? triggerTestId}
            key={tab.id}
            value={tab.value}
          >
            {tab.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
    );
  },
);

PanelTabsList.displayName = 'PanelTabsList';
